import type { Estimator } from '../types';

/** Display option for a Project Manager select. */
export interface ProjectManagerOption {
  id: number;
  name: string;
}

/**
 * Build the Project Manager dropdown options, merging in the current
 * assignee when they are absent from the fetched PM list (e.g. a user
 * whose role changed, or who was deactivated, after being assigned).
 *
 * Without this merge the select silently falls back to its placeholder
 * ("Unassigned" / "Select project manager") even though the underlying
 * form data still holds the real assignee id. Mirrors the
 * inactive-insurance-company merge pattern in LeadForm.
 *
 * The stranded assignee is appended after the fetched list (which is
 * already sorted by last name) rather than re-sorted, because only a
 * single display-name string is available for them.
 */
export function buildProjectManagerOptions(
  projectManagers: Estimator[],
  assignedToUserId?: number | null,
  assignedToUserName?: string | null,
): ProjectManagerOption[] {
  const options: ProjectManagerOption[] = projectManagers.map((pm) => ({
    id: pm.id,
    name: `${pm.firstName} ${pm.lastName}`,
  }));

  if (
    assignedToUserId != null &&
    !options.some((option) => option.id === assignedToUserId)
  ) {
    options.push({
      id: assignedToUserId,
      name: assignedToUserName || `User #${assignedToUserId}`,
    });
  }

  return options;
}
